import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { TagColor } from '@equip.health/ui';

import { NoteStatus } from '~/lib/constants/AddPatientNoteModal.constants';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import {
  PatientUniversalStatusCode,
  TreatmentEpisodeStatus,
} from '~/lib/constants/patients.constants';
import {
  determinePatientInquiryStatus,
  treatmentEpisodeColorMapper,
} from '~/lib/util/patient.util';
import {
  formatInquiryText,
  formatTreatmentEpisodeText,
  inquiryStatusStylesMapper,
} from '~/lib/util/patientStatusHeader.util';
import {
  useGetPatientTreatmentEpisodesQuery,
} from '~/store/services/clinicalData';
import { useGetDischargePlanByIdQuery } from '~/store/services/clinical-data/dischargePlan';
import {
  useGetInquiryDetailByIdQuery,
  useGetPatientInquiriesSummaryQuery,
} from '~/store/services/inquiry';
import { useGetPatientNotesQuery } from '~/store/services/notes/notes';
import { selectDischargePlanEpisode } from '../tabs/careplan/dischargeplan/util/dischargePlanUtils';
import { useInsuranceAuthorizationEndDate } from '~/lib/hooks/useInsuranceAuthorizationEndDate';

type UsePatientStatusHeaderDataProps = {
  patientId: string;
  patientStatus: string;
  patientStatusCode: string;
  patientStatusColor: TagColor;
  primaryDiagnosis?: string;
};

const defaultStatusLine = 'Unknown status';

export const usePatientStatusHeaderData = ({
  patientId,
  patientStatus,
  patientStatusCode,
  patientStatusColor,
  primaryDiagnosis,
}: UsePatientStatusHeaderDataProps) => {
  const {
    displayInsuranceAuthEndingDates: isDisplayInsuranceAuthEndingDatesEnabled,
    enableDisplayInsurancePart1: isEnableDisplayInsurancePart1,
  } = useFlags<LDFlags>();

  const isInsuranceAuthEndingDatesActive =
    isDisplayInsuranceAuthEndingDatesEnabled &&
    isEnableDisplayInsurancePart1;

  const {
    data: patientTreatmentEpisodes,
    isLoading: isTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId);

  const { data: patientInquirySummaries, isFetching: isFetchingSummary } =
    useGetPatientInquiriesSummaryQuery(patientId);

  const activeInquiry: GetPatientInquiriesSummaryResponse = useMemo(() => {
    return determinePatientInquiryStatus(
      patientInquirySummaries,
      patientStatus,
    );
  }, [patientInquirySummaries, patientStatus]);

  const { data: activeInquiryDetail, isLoading: isActiveInquiryDetailLoading } =
    useGetInquiryDetailByIdQuery(activeInquiry?.id, {
      refetchOnMountOrArgChange: true,
      skip: !activeInquiry,
    });

  const { data: notes } = useGetPatientNotesQuery(
    { inquiryId: activeInquiryDetail?.id },
    {
      refetchOnMountOrArgChange: true,
      skip: activeInquiryDetail?.id == null,
    },
  );

  const lastContactDateISO: string | null | undefined = useMemo(() => {
    if (!Array.isArray(notes)) {
      return null;
    }

    return notes
      .filter(({ noteStatus }) => noteStatus !== NoteStatus.DRAFT)
      .sort(
        (a, b) =>
          new Date(b.createdDateTime).getTime() -
          new Date(a.createdDateTime).getTime(),
      )[0]?.createdDateTime;
  }, [notes]);

  const isInquiryStatus = useMemo(
    () =>
      Object.values(InquiryStatusValue).includes(
        patientStatus as InquiryStatusValue,
      ),
    [patientStatus],
  );

  const isTreatmentEpisodeStatus = useMemo(
    () =>
      Object.values(TreatmentEpisodeStatus).includes(
        patientStatus as TreatmentEpisodeStatus,
      ),
    [patientStatus],
  );

  const treatmentEpisode = useMemo(() => {
    if (!isTreatmentEpisodeStatus) {
      return undefined;
    }

    const statusCodeToMatch =
      patientStatusCode === PatientUniversalStatusCode.PAUSED
        ? PatientUniversalStatusCode.IN_TREATMENT
        : patientStatusCode;

    return patientTreatmentEpisodes?.data?.find(
      (episode) => episode.status.code === statusCodeToMatch,
    );
  }, [isTreatmentEpisodeStatus, patientStatusCode, patientTreatmentEpisodes]);

  const { episode: selectedEpisode } = useMemo(
    () => selectDischargePlanEpisode(patientTreatmentEpisodes?.data ?? []),
    [patientTreatmentEpisodes?.data],
  );

  const { data: dischargePlanData, isLoading: isGetDischargePlanLoading } =
    useGetDischargePlanByIdQuery(selectedEpisode?.dischargePlan?.id, {
      skip: !selectedEpisode?.dischargePlan?.id || !isEnableDisplayInsurancePart1,
    });

  const {
    billingPeriods,
    insuranceAuthorizationEndDate,
    isGetBillingPeriodsLoading,
    isGetMedicalCoverageLoading,
    medicalCoverage,
  } = useInsuranceAuthorizationEndDate({
    enabled: isInsuranceAuthEndingDatesActive,
    patientId,
  });

  const displayData: PatientStatusCardDisplayData = useMemo(() => {
    const display: PatientStatusCardDisplayData = {
      lineOne: defaultStatusLine,
      lineTwo: defaultStatusLine,
      statusColor: patientStatusColor,
    };

    if (
      isInquiryStatus &&
      activeInquiryDetail?.status.name === InquiryStatusValue[patientStatusCode]
    ) {
      const inquiryDisplay = formatInquiryText(
        InquiryStatusValue[patientStatusCode] as InquiryStatusValue,
        activeInquiryDetail,
        lastContactDateISO,
      );

      display.lineOne = inquiryDisplay.lineOne;
      display.lineTwo = inquiryDisplay.lineTwo;

      const inquiryStatusInfo =
        inquiryStatusStylesMapper[InquiryStatusValue[patientStatusCode]];
      if (inquiryStatusInfo) {
        display.statusColor = inquiryStatusInfo.tagColor;
      }
    }

    if (isTreatmentEpisodeStatus && patientTreatmentEpisodes?.data.length > 0) {
      if (treatmentEpisode) {
        const treatmentDisplay = formatTreatmentEpisodeText(
          patientStatusCode,
          treatmentEpisode,
          primaryDiagnosis,
          isInsuranceAuthEndingDatesActive
            ? dischargePlanData?.tentativeDischargeDate
            : undefined,
        );

        display.lineOne = treatmentDisplay.lineOne;
        display.lineTwo = treatmentDisplay.lineTwo;

        if (patientStatusCode !== PatientUniversalStatusCode.PAUSED) {
          display.statusColor =
            treatmentEpisodeColorMapper[treatmentEpisode.status.description] ??
            display.statusColor;
        }
      }
    }

    return display;
  }, [
    activeInquiryDetail,
    dischargePlanData,
    isInquiryStatus,
    isTreatmentEpisodeStatus,
    lastContactDateISO,
    patientStatusCode,
    patientStatusColor,
    patientTreatmentEpisodes,
    primaryDiagnosis,
    treatmentEpisode,
  ]);

  return {
    activeInquiryDetail,
    billingPeriods,
    displayData,
    insuranceAuthorizationEndDate,
    isLoading:
      isTreatmentEpisodesLoading ||
      isFetchingSummary ||
      isActiveInquiryDetailLoading ||
      isGetDischargePlanLoading ||
      isGetBillingPeriodsLoading ||
      isGetMedicalCoverageLoading,
    lastContactDateISO,
    medicalCoverage,
    tentativeDischargeDate: dischargePlanData?.tentativeDischargeDate,
    treatmentEpisode,
  };
};
